import React from "react";
import { 
  FaRocket, 
  FaShieldAlt, 
  FaChartLine, 
  FaUsers, 
  FaLightbulb,
  FaGlobe,
  FaHandshake,
  FaLock
} from "react-icons/fa";

const AboutUs = () => {
    return (
        <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
            {/* Hero Section */}
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
                <div className="text-center mb-16">
                    <h1 className="text-5xl font-bold text-gray-900 mb-6">
                        About RiskDiscover
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Powered by cutting-edge AI technology, RiskDiscover revolutionizes how businesses 
                        assess and manage risk in the European market.
                    </p>
                </div>

                {/* What We Do */}
                <div className="bg-white rounded-2xl shadow-xl p-10 mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                        Intelligent Risk Assessment for Modern Businesses
                    </h2>
                    
                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">
                                AI-Powered Intelligence
                            </h3>
                            <p className="text-gray-600 leading-relaxed">
                                RiskDiscover leverages advanced artificial intelligence to analyze complex 
                                business data, providing comprehensive risk assessments that help companies 
                                make informed decisions with confidence. Our platform processes multiple 
                                data points to deliver accurate, actionable insights.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">
                                European Market Expertise
                            </h3>
                            <p className="text-gray-600 leading-relaxed">
                                Specifically designed for the European business landscape, RiskDiscover 
                                understands the unique challenges and regulatory requirements of operating 
                                in EU markets. Our assessments consider local market conditions, compliance 
                                requirements, and regional business practices.
                            </p>
                        </div>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-600">
                        <p className="text-gray-700 italic">
                            "We believe every business deserves access to enterprise-grade risk intelligence. 
                            RiskDiscover makes sophisticated risk assessment accessible, affordable, and 
                            actionable for companies of all sizes."
                        </p>
                    </div>
                </div>

                {/* Our Mission */}
                <div className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                        Our Mission
                    </h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                            <FaLightbulb className="text-3xl text-yellow-500 mb-4" />
                            <h3 className="text-xl font-semibold mb-3">Democratize Risk Intelligence</h3>
                            <p className="text-gray-600">
                                Make enterprise-grade risk assessment accessible to businesses of all sizes 
                                through affordable, AI-powered solutions.
                            </p>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                            <FaShieldAlt className="text-3xl text-green-500 mb-4" />
                            <h3 className="text-xl font-semibold mb-3">Enhance Business Security</h3>
                            <p className="text-gray-600">
                                Provide comprehensive insights that help companies identify, understand, 
                                and mitigate potential risks before they impact operations.
                            </p>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                            <FaChartLine className="text-3xl text-blue-500 mb-4" />
                            <h3 className="text-xl font-semibold mb-3">Drive Informed Decisions</h3>
                            <p className="text-gray-600">
                                Empower business leaders with data-driven insights that support strategic 
                                planning and sustainable growth.
                            </p>
                        </div>
                    </div>
                </div>

                {/* What Sets Us Apart */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-10 text-white mb-12">
                    <h2 className="text-3xl font-bold mb-8 text-center">
                        What Sets RiskDiscover Apart
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="text-center">
                            <FaRocket className="text-4xl mb-3 mx-auto" />
                            <h4 className="font-semibold mb-2">AI-Powered Analysis</h4>
                            <p className="text-sm opacity-90">
                                Advanced machine learning models trained on vast datasets
                            </p>
                        </div>
                        <div className="text-center">
                            <FaGlobe className="text-4xl mb-3 mx-auto" />
                            <h4 className="font-semibold mb-2">EU Market Focus</h4>
                            <p className="text-sm opacity-90">
                                Specialized in European business environments and regulations
                            </p>
                        </div>
                        <div className="text-center">
                            <FaUsers className="text-4xl mb-3 mx-auto" />
                            <h4 className="font-semibold mb-2">User-Centric Design</h4>
                            <p className="text-sm opacity-90">
                                Intuitive interface designed for business professionals
                            </p>
                        </div>
                        <div className="text-center">
                            <FaHandshake className="text-4xl mb-3 mx-auto" />
                            <h4 className="font-semibold mb-2">Trusted Partner</h4>
                            <p className="text-sm opacity-90">
                                Committed to your business success and data security
                            </p>
                        </div>
                    </div>
                </div>

                {/* How It Works */}
                <div className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                        How RiskDiscover Works
                    </h2>
                    <div className="bg-white rounded-xl shadow-lg p-8">
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="text-center">
                                <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                                    <span className="text-blue-600 font-bold text-lg">1</span>
                                </div>
                                <h3 className="font-semibold text-lg mb-3 text-gray-800">
                                    Input Company Data
                                </h3>
                                <p className="text-gray-600">
                                    Simply enter a company's fiscal number and location to begin the assessment process.
                                </p>
                            </div>
                            <div className="text-center">
                                <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                                    <span className="text-blue-600 font-bold text-lg">2</span>
                                </div>
                                <h3 className="font-semibold text-lg mb-3 text-gray-800">
                                    AI Analysis
                                </h3>
                                <p className="text-gray-600">
                                    Our AI analyzes multiple data sources, including business records, location data, 
                                    and market conditions.
                                </p>
                            </div>
                            <div className="text-center">
                                <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                                    <span className="text-blue-600 font-bold text-lg">3</span>
                                </div>
                                <h3 className="font-semibold text-lg mb-3 text-gray-800">
                                    Comprehensive Report
                                </h3>
                                <p className="text-gray-600">
                                    Receive a detailed risk assessment with location, sector-specific, and operational insights.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Security & Compliance */}
                <div className="bg-gray-50 rounded-xl p-8 mb-12">
                    <div className="flex items-center justify-center mb-6">
                        <FaLock className="text-3xl text-gray-700 mr-3" />
                        <h2 className="text-2xl font-bold text-gray-900">
                            Security & Compliance
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        <ul className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="text-green-500 mr-2">✓</span>
                                GDPR Compliant data handling
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-500 mr-2">✓</span>
                                Encrypted data transmission and storage
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-500 mr-2">✓</span>
                                Regular security audits and updates
                            </li>
                        </ul>
                        <ul className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="text-green-500 mr-2">✓</span>
                                Secure API architecture
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-500 mr-2">✓</span>
                                ISO-compliant infrastructure
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-500 mr-2">✓</span>
                                No data sharing with third parties
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Our Commitment */}
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">
                        Our Commitment to You
                    </h2>
                    <div className="max-w-3xl mx-auto">
                        <p className="text-lg text-gray-600 mb-4">
                            RiskDiscover is committed to continuous innovation and improvement. 
                            We're not just a platform - we're a growing ecosystem that evolves with 
                            your needs and the changing business landscape.
                        </p>
                        <p className="text-lg text-gray-600 mb-4">
                            We believe in transparency, reliability, and building long-term partnerships 
                            with our clients. Your success is our success, and we're dedicated to providing 
                            you with the tools and insights you need to thrive in today's competitive market.
                        </p>
                    </div>
                </div>

                {/* Contact CTA */}
                <div className="bg-gray-900 rounded-2xl p-10 text-center">
                    <h3 className="text-2xl font-bold text-white mb-4">
                        Ready to Transform Your Risk Management?
                    </h3>
                    <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                        Join thousands of European businesses that trust RiskDiscover 
                        for their critical business intelligence needs.
                    </p>
                    <div className="flex gap-4 justify-center">
                        <a 
                            href="/products" 
                            className="bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition-all hover:shadow-xl transform hover:scale-105"
                        >
                            Get Started Free
                        </a>
                        <a 
                            href="/contact" 
                            className="bg-white text-gray-900 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all transform hover:scale-105"
                        >
                            Contact Us
                        </a>
                    </div>
                </div>

                {/* Footer Note */}
                <div className="text-center mt-12">
                    <p className="text-sm text-gray-500">
                        RiskDiscover.com © {new Date().getFullYear()} | All rights reserved
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                        Developed by{" "}
                        <a 
                            href="https://www.moredevs.ai" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 underline"
                        >
                            MoreDevs.ai
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AboutUs;